import React from 'react';
import { TrendingUp, Clock } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SelectSeparator,
    SelectGroup,
    SelectLabel,
} from '@/components/ui/select';
import { toast } from 'react-toastify';

export type SortOption = 'relevance' | 'recent';

interface SortControlsProps {
    currentSort: SortOption;
    onChange: (value: SortOption) => void;
    isMobile: boolean;
}

const SortControls: React.FC<SortControlsProps> = ({
    currentSort,
    onChange,
    isMobile
}) => {
    // Mobile toggle button handler
    const toggleSortOption = () => {
        const newSort = currentSort === 'relevance' ? 'recent' : 'relevance';
        onChange(newSort);

        // Show toast notification (reused from DiscoverMainSection)
        toast.success(`Sorting by ${newSort === 'relevance' ? 'Relevance' : 'Recent'}`, {
            position: 'bottom-center',
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    // For mobile: render toggle button
    if (isMobile) {
        return (
            <button
                onClick={toggleSortOption}
                className="flex items-center justify-center p-1 h-9 rounded-md transition-colors mobile-touch-target bg-white border border-gray-200 hover:bg-gray-50 min-h-[40px] min-w-[40px] focus:outline-none flex-shrink-0"
                aria-label={`Sort by ${currentSort === 'relevance' ? 'Recent' : 'Relevance'}`}
                title={`Switch to ${currentSort === 'relevance' ? 'Recent' : 'Relevance'} sorting`}
            >
                {/* Show icon for what will happen on click, not current state */}
                {currentSort === 'relevance' ? (
                    <Clock className="h-5 w-5 text-purple-500" />
                ) : (
                    <TrendingUp className="h-5 w-5 text-purple-500" />
                )}
            </button>
        );
    }

    // For desktop: render dropdown
    return (
        <Select
            onValueChange={(value) => onChange(value as SortOption)}
            defaultValue={currentSort}
            value={currentSort}
        >
            <SelectTrigger className="w-36 bg-white border-gray-200 hover:bg-gray-50 transition-colors duration-150 font-medium focus:outline-none">
                <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="border border-gray-200 shadow-md rounded-lg min-w-[180px] p-1"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}>

                <SelectGroup>
                    <SelectLabel className="text-xs text-gray-500 font-semibold px-2 py-1">Sort Options</SelectLabel>
                    <SelectSeparator className="my-1" />
                    <SelectItem
                        value="relevance"
                        className="flex items-center rounded-md hover:bg-purple-50 cursor-pointer transition-colors duration-150 focus:bg-purple-50 focus:text-purple-800"
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center">
                            <TrendingUp className="h-4 w-4 mr-2 text-purple-500" />
                            <span>Relevance</span>
                        </div>
                    </SelectItem>
                    <SelectItem
                        value="recent"
                        className="flex items-center rounded-md hover:bg-purple-50 cursor-pointer transition-colors duration-150 focus:bg-purple-50 focus:text-purple-800"
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-purple-500" />
                            <span>Recent</span>
                        </div>
                    </SelectItem>
                </SelectGroup>
            </SelectContent>
        </Select>
    );
};

export default SortControls;
